import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ContactFieldsEditor } from "@/components/party/ContactFieldsEditor";
import { useUpdateTenant } from "@/hooks/api/useParties";
import { CONTACT_TEXT_FIELDS, buildContactPayload } from "@/lib/contactFields";
import type { components } from "@/types/api";

type Tenant = components["schemas"]["TenantResponse"];

const TENANT_TYPES = ["local", "expat", "sharers", "student", "gv"];
const ID_TYPES = ["passport", "id_card", "driver_license"];
const SELECT = "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm";

export function TenantDetailsCard({ tenant }: { tenant: Tenant }) {
  const update = useUpdateTenant(tenant.id);
  const [editing, setEditing] = useState(false);
  const c = tenant.contact as Record<string, unknown>;

  const [contact, setContact] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {
      title: (c.title as string) ?? "",
      gender: (c.gender as string) ?? "",
      language: (c.language as string) ?? "nl",
    };
    for (const { key } of CONTACT_TEXT_FIELDS) init[key] = (c[key] as string) ?? "";
    return init;
  });
  const [form, setForm] = useState({
    tenant_type: tenant.tenant_type ?? "",
    identification_type: tenant.identification_type ?? "",
    identification_number: tenant.identification_number ?? "",
    iban: tenant.iban ?? "",
    bank_account_name: tenant.bank_account_name ?? "",
  });
  const [isSubscriber, setIsSubscriber] = useState(tenant.is_subscriber);
  const [isActive, setIsActive] = useState(tenant.is_active);

  const setC = (k: string, v: string) => setContact((s) => ({ ...s, [k]: v }));

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(
      {
        contact: buildContactPayload(contact, "person"),
        tenant_type: form.tenant_type || null,
        identification_type: form.identification_type || null,
        identification_number: form.identification_number || null,
        iban: form.iban || null,
        bank_account_name: form.bank_account_name || null,
        is_subscriber: isSubscriber,
        is_active: isActive,
      },
      {
        onSuccess: () => {
          toast.success("Tenant updated");
          setEditing(false);
        },
        onError: () => toast.error("Could not update tenant"),
      },
    );
  };

  if (editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Edit tenant</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5">
            <ContactFieldsEditor form={contact} set={setC} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tenant_type">Tenant type</Label>
                <select
                  id="tenant_type"
                  value={form.tenant_type}
                  onChange={(e) => setForm({ ...form, tenant_type: e.target.value })}
                  className={SELECT}
                >
                  <option value="">—</option>
                  {TENANT_TYPES.map((t) => (
                    <option key={t} value={t} className="capitalize">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="identification_type">ID type</Label>
                <select
                  id="identification_type"
                  value={form.identification_type}
                  onChange={(e) =>
                    setForm({ ...form, identification_type: e.target.value })
                  }
                  className={SELECT}
                >
                  <option value="">—</option>
                  {ID_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="identification_number">ID number</Label>
                <Input
                  id="identification_number"
                  value={form.identification_number}
                  onChange={(e) =>
                    setForm({ ...form, identification_number: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  value={form.iban}
                  onChange={(e) => setForm({ ...form, iban: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bank_account_name">Account holder</Label>
                <Input
                  id="bank_account_name"
                  value={form.bank_account_name}
                  onChange={(e) =>
                    setForm({ ...form, bank_account_name: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isSubscriber}
                  onChange={(e) => setIsSubscriber(e.target.checked)}
                  className="size-4 rounded border-input accent-primary"
                />
                Subscriber
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="size-4 rounded border-input accent-primary"
                />
                Active
              </label>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save tenant"}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  const row = (label: string, value: unknown) => (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value ? String(value) : "—"}</dd>
    </div>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Details</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
          <Pencil className="size-4" />
          Edit
        </Button>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          {row("Email", tenant.contact.email)}
          {row("Phone", tenant.contact.phone ?? tenant.contact.mobile)}
          {row("ID type", tenant.identification_type)}
          {row("ID number", tenant.identification_number)}
          {row("IBAN", tenant.iban)}
          {row("Account holder", tenant.bank_account_name)}
        </dl>
      </CardContent>
    </Card>
  );
}
